import React from 'react';
import {
  AvMessagePopover,
  ConditionType,
  defaultOperator,
  DescriptorType,
  Filter,
  getEmptyExpression,
  getRelationalExpressionProps,
  OpenSelect,
  Select,
  selectFieldWidth,
  StringConditionType,
} from '@fabric-ui/components';
import { ArrowDown } from '@fabric-ui/icons';
import { InfoFull } from '@fabric-ui/icons/colorful';
import { emptyObject } from '@fabric-ui/utils';
import { AutocompleteRenderGroupParams, Box, Divider, List, ListItem, Typography } from '@mui/material';
import { fieldTypeIconsMap, fieldTypeLabelMap, getFieldKey } from '../../../views/ModelManagement/hooks';
import { FieldType, FieldTypeEnum, SchemaTypes, typeLabelMap } from '../../../views/Sources/Mapping/types';
import WithDescriptionTooltip from '../../WithDescriptionTooltip/WithDescriptionTooltip';
import { ANY_VALUE } from '../constants';
import { CustomFieldHandler, useExpressionBuilder } from '../ExpressionBuilderContext';
import { useFilterConfig, useFilterContext } from '../FilterContext';

export const FREE_SEARCH_FIELD = '__free_search__';
export const isFreeSearchField = (fieldName?: string | null): boolean => fieldName === FREE_SEARCH_FIELD;

type SelectableDescriptorType = DescriptorType | DescriptorType[];

export const getConditionInfoFromDescriptor = (descriptorType: SelectableDescriptorType) => {
  const isRepeated = Array.isArray(descriptorType);
  const underlyingDescriptorType = isRepeated ? descriptorType[0] : descriptorType;
  return {
    isRepeated,
    typeCondition: ConditionType[typeLabelMap[underlyingDescriptorType]] ?? ConditionType.string,
  };
};

interface FieldSelectorSectionProps {
  enableFilter: boolean;
  fieldName?: string | null;
  filter: Filter;
  isIngressMode: boolean;
  isRepeated: boolean;
  onAutoFocusFieldSelected?: (fieldName: string) => void;
  setCorrectFilter: (filter: Filter) => void;
  setFilter: (filter: Filter) => void;
  typeCondition: ConditionType;
}

export const FieldSelectorSection: React.FC<FieldSelectorSectionProps> = ({
  enableFilter,
  fieldName,
  filter,
  isIngressMode,
  isRepeated,
  onAutoFocusFieldSelected,
  setCorrectFilter,
  setFilter,
  typeCondition,
}) => {
  const {
    canAddNewFields = false,
    canSelectFieldType = true,
    customFieldHandlers = emptyObject as Record<string, CustomFieldHandler>,
    fieldSelectionMessage,
    fields = [],
    fieldsListAdornment,
    ingressModeProps,
    isLoadingFields = false,
    readOnly = false,
    relationsProps = {},
    supportParser = false,
  } = useExpressionBuilder();
  const { disabled, isVertical, size } = useFilterConfig();
  const { accountEntities } = useFilterContext();
  const fieldMap = accountEntities?.fieldMap ?? {};
  const iconsMap = fieldTypeIconsMap({ color: 'var(--color-content-base-secondary)' });
  const conditionType = Object.keys(ConditionType).find(type => ConditionType[type] === typeCondition) || SchemaTypes.STRING;
  const fieldType = fieldMap[isIngressMode ? (fieldName || '').replace('.sources', '') : fieldName!]?.type || conditionType;
  const selectedField = fields.find(field => field.value === fieldName);
  const SelectComponent = canAddNewFields ? OpenSelect : Select;
  const labelFunc = ({ descriptorType }) => (
    <>
      {iconsMap[getFieldKey(descriptorType)]} {fieldTypeLabelMap[getFieldKey(descriptorType)]?.label}
    </>
  );
  const typeOptions: { descriptorType: SelectableDescriptorType; value: ConditionType }[] = [
    ...[FieldType.Text, FieldType.DateTime, FieldType.Number, FieldType.Boolean].map(type => {
      const descriptorType = FieldTypeEnum[type] as DescriptorType;
      return { descriptorType, value: getConditionInfoFromDescriptor(descriptorType).typeCondition };
    }),
    { descriptorType: [FieldTypeEnum[FieldType.Text] as DescriptorType], value: ConditionType.array },
  ];

  const onChangeFieldSelect = (
    value: string | null | undefined,
    { type = FieldType.Text, repeated }: { type?: string; repeated?: boolean } = {}
  ) => {
    if (!value) return;

    const customHandler = customFieldHandlers[value];
    if (customHandler) {
      if (customHandler.autoFocusOnSelect) {
        onAutoFocusFieldSelected?.(value);
      }
      setFilter(customHandler.getEmptyFilter?.() ?? {});
      return;
    }

    const newTypeCondition = ConditionType[typeLabelMap[type]] || typeCondition;
    const defaultNewOperator = supportParser
      ? value === ANY_VALUE
        ? StringConditionType.CONTAINS
        : StringConditionType.EQUALS
      : repeated && newTypeCondition === ConditionType.field
        ? 'in'
        : undefined;
    const newExpression = getEmptyExpression(value, newTypeCondition, defaultNewOperator, fieldMap[value]).expression;
    setCorrectFilter({
      expression: {
        ...newExpression,
        ...getRelationalExpressionProps({
          operator: defaultNewOperator ?? defaultOperator[newTypeCondition],
          typeCondition: newTypeCondition,
          enableSide: !!relationsProps.enableSide,
          enableCaseSensitive: !!relationsProps.enableCaseSensitive,
        }),
      },
    });
  };

  const startAdornment = () => {
    if (selectedField?.icon || selectedField?.groupIcon) {
      return (
        <Box className="-ml-2 -mr-2 flex size-[18px] shrink-0 items-center justify-center rounded-full bg-[var(--color-surface-base-tertiary)]">
          {selectedField.icon || selectedField.selectedGroupIcon || selectedField.groupIcon}
        </Box>
      );
    }
    const selectedConditionType = isRepeated ? ConditionType.array : typeCondition;
    return canAddNewFields && canSelectFieldType ? (
      <Select
        variant="outlined"
        size="xSmall"
        showInput={false}
        value={selectedConditionType as any}
        onChange={(_, option: any) => {
          const { isRepeated: nextIsRepeated, typeCondition: nextTypeCondition } = getConditionInfoFromDescriptor(option.descriptorType);
          const selectedConditionType = nextIsRepeated ? ConditionType.array : nextTypeCondition;
          const emptyExpressionField = (canSelectFieldType ? fieldName : filter.expression?.fieldName) ?? null;
          setFilter({
            ...filter,
            expression: filter.expression?.[selectedConditionType]
              ? filter.expression
              : getEmptyExpression(
                  emptyExpressionField,
                  nextTypeCondition,
                  supportParser ? StringConditionType.EQUALS : undefined,
                  emptyExpressionField ? fieldMap[emptyExpressionField] : undefined,
                  nextIsRepeated
                ).expression,
          });
        }}
        options={typeOptions as any}
        getLabelFunc={labelFunc as any}
        selectIcon={
          <Box className="flex h-[18px] items-center gap-px text-content-base-secondary">
            {iconsMap[getFieldKey(typeOptions.find(({ value }) => value === selectedConditionType)?.descriptorType)]}
            <ArrowDown />
          </Box>
        }
        style={{ width: 52, pl: '3px', ml: '-12px', mr: '-8px' }}
        disabled={disabled}
      />
    ) : (
      <Box className="z-[2] -mr-2 flex items-center">
        {iconsMap[isRepeated ? JSON.stringify(fieldType) : (fieldType as string | number)]}
      </Box>
    );
  };

  return (
    <SelectComponent
      style={{ width: isVertical ? selectFieldWidth : 250 }}
      size={size}
      isRequired
      placeholder="Select Field"
      value={fieldName}
      groupByFunc={({ group }) => group}
      onChange={onChangeFieldSelect}
      options={fields}
      loading={isLoadingFields}
      getValueFunc={isIngressMode && ingressModeProps ? ({ ingressValue }) => ingressValue : undefined}
      skeletonLoading
      optionsUseQueryProps={{ options: fields, isLoading: isLoadingFields }}
      startAdornment={
        !relationsProps.hideStartAdornment &&
        fieldName &&
        (selectedField?.icon || selectedField?.groupIcon || !supportParser) &&
        startAdornment
      }
      disabled={!enableFilter || disabled}
      readOnly={readOnly}
      renderOption={(option: (typeof fields)[number]) => (
        <WithDescriptionTooltip title={option.description} badgeProps={{ className: 'w-full' }} badgify>
          <Box className="flex items-center gap-1">
            {option.icon}
            {option.title}
          </Box>
        </WithDescriptionTooltip>
      )}
      inputToolbar={fieldsListAdornment}
      muiProps={{
        ...(canAddNewFields && { autoHighlight: true }),
        ...((fieldSelectionMessage || fields.some(field => field.groupIcon)) && {
          ...(fieldSelectionMessage && { getOptionDisabled: option => option.disabled === true }),
          renderGroup: ({ group, children, key }: AutocompleteRenderGroupParams) =>
            !group ? (
              <React.Fragment key={key}>{children}</React.Fragment>
            ) : (
              <ListItem key={key} className="flex flex-col p-0">
                <Divider className="w-full py-0.5" />
                <Box className="sticky -top-px z-[1] flex h-10 w-full items-center justify-between bg-surface-base-primary px-2">
                  <Box className="flex items-center gap-1">
                    {fields.find(field => field.group === group)?.groupIcon}
                    <Typography variant="h7">{group}</Typography>
                  </Box>
                  {fieldSelectionMessage && <AvMessagePopover icon={<InfoFull />} message={fieldSelectionMessage} asTooltip />}
                </Box>
                <List className="w-full p-0">{children}</List>
              </ListItem>
            ),
        }),
      }}
    />
  );
};
